// Shared @-mention / #-asset input — used by both seller-side (BuyerSpaceEditors
// ConversationsEditor + the People-tab composer) and prospect-side
// (BuyerSpaceProspectView ProspectConversationsPanel) of the Decision
// Room conversation thread.
//
// Behavior:
//   - Plain textarea with autocomplete popup: typing @ opens a list
//     of taggable people (filtered by what comes after the @).
//   - Typing # opens a list of room ASSETS (documents, trailers) when
//     the caller passed some — rooms_message_asset_links (2026-09-10).
//     Without an `assets` prop, # is just a character.
//   - Up/Down to navigate, Enter or click to pick. Esc dismisses.
//   - Picking inserts "@<displayName> " / "#<title> " at the caret + adds
//     the person / asset to the mentions / assetRefs array. Caller reads
//     them via onMentionsChange / onAssetRefsChange.
//   - At submit time, the parent should call filterMentionsAgainstBody()
//     and filterAssetRefsAgainstBody() to strip entries whose text the
//     user backspaced out of the body before posting. That keeps the
//     persisted arrays honest so the backend's notification dispatch
//     doesn't spam and no chip renders for text that isn't there.
//
// Design choices:
//   - No contenteditable / rich-text trickery — keeps the body a plain
//     string, easy to render anywhere (email, plaintext fallbacks).
//   - The mentions / assetRefs arrays are the source of truth for *what*
//     got tagged; body text is just human-readable surface.

import { useEffect, useMemo, useRef, useState } from 'react';

const DOC_ICON = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);
const TRAILER_ICON = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

export function assetKindLabel(kind) {
  return kind === 'trailer' ? 'Trailer' : 'Document';
}

export default function MentionInput({
  value,
  onChange,
  mentions,
  onMentionsChange,
  people = [],
  // rooms_message_asset_links: [{ kind: 'doc'|'trailer', id, title, section, subtitle }]
  assets = [],
  assetRefs,
  onAssetRefsChange,
  placeholder,
  rows = 3,
  onKeyDown: parentOnKeyDown,
  style,
}) {
  const taRef = useRef(null);
  const assetsEnabled = Array.isArray(assets) && assets.length > 0 && typeof onAssetRefsChange === 'function';
  // trigger: which popup is open ('@' people, '#' assets) — null when
  // closed. query: the text typed AFTER the trigger character.
  const [trigger, setTrigger] = useState(null);
  const [query, setQuery] = useState('');
  const [anchor, setAnchor] = useState(0); // caret pos at the trigger char
  const [activeIdx, setActiveIdx] = useState(0);

  // Filter the candidate list against the typed query. People match on
  // name / email / role; assets on title / kind. Cap at 6 so the popup
  // stays compact.
  const matches = useMemo(() => {
    if (trigger == null) return [];
    const q = query.trim().toLowerCase();
    if (trigger === '@') {
      return people.filter((p) => {
        if (!p.fullName) return false;
        if (!q) return true;
        const hay = `${p.fullName} ${p.email || ''} ${p.role || ''} ${p.title || ''}`.toLowerCase();
        return hay.includes(q);
      }).slice(0, 6);
    }
    return assets.filter((a) => {
      if (!a || !a.title) return false;
      if (!q) return true;
      return `${a.title} ${assetKindLabel(a.kind)} ${a.subtitle || ''}`.toLowerCase().includes(q);
    }).slice(0, 6);
  }, [trigger, query, people, assets]);

  // Keep activeIdx in range as matches change (don't keep highlighting
  // a row that scrolled out of the filter).
  useEffect(() => {
    if (activeIdx >= matches.length) setActiveIdx(0);
  }, [matches.length, activeIdx]);

  const closeSuggestions = () => {
    setTrigger(null);
    setQuery('');
    setAnchor(0);
    setActiveIdx(0);
  };

  // Detect a trigger on every change. Simple rule: scan back from the
  // caret to the nearest whitespace; if it starts with @ (or #, when
  // assets are on) and the chars after are a short fragment, open
  // suggestions. Otherwise close them.
  const onValueChange = (newVal, caretPos) => {
    onChange(newVal);
    if (caretPos == null) caretPos = newVal.length;
    let i = caretPos - 1;
    while (i >= 0 && !/\s/.test(newVal[i])) {
      const ch = newVal[i];
      if (ch === '@' || (ch === '#' && assetsEnabled)) {
        const fragment = newVal.slice(i + 1, caretPos);
        // Bound the popup to short fragments; if user has typed a
        // 30-char rant after the trigger they're probably not picking.
        if (fragment.length <= 40 && !/[@#]/.test(fragment)) {
          setAnchor(i);
          setTrigger(ch);
          setQuery(fragment);
          return;
        }
        break;
      }
      i--;
    }
    closeSuggestions();
  };

  const handleChange = (e) => {
    onValueChange(e.target.value, e.target.selectionStart);
  };

  // Replace text from `anchor` to the caret with the insertion + a
  // trailing space so the user can keep typing without re-triggering.
  const spliceAtAnchor = (insertion) => {
    const ta = taRef.current;
    if (!ta) return;
    const caret = ta.selectionStart;
    const before = value.slice(0, anchor);
    const after = value.slice(caret);
    onChange(before + insertion + after);
    closeSuggestions();
    // Restore caret to right after the insertion. setTimeout because
    // React's value update happens after this synchronous tick.
    const newCaret = before.length + insertion.length;
    setTimeout(() => {
      try { ta.setSelectionRange(newCaret, newCaret); ta.focus(); } catch (_) { /* noop */ }
    }, 0);
  };

  const insertMention = (person) => {
    if (!person) return;
    spliceAtAnchor(`@${person.fullName} `);
    // Dedupe on personId — picking the same person twice doesn't
    // double-count.
    const existing = Array.isArray(mentions) ? mentions : [];
    if (!existing.some((m) => m.personId === person.id)) {
      onMentionsChange([
        ...existing,
        {
          personId: person.id,
          displayName: person.fullName,
          email: person.email || null,
          side: person.side || null,
        },
      ]);
    }
  };

  const insertAsset = (asset) => {
    if (!asset) return;
    spliceAtAnchor(`#${asset.title} `);
    const existing = Array.isArray(assetRefs) ? assetRefs : [];
    if (!existing.some((r) => r.kind === asset.kind && r.id === asset.id)) {
      onAssetRefsChange([
        ...existing,
        { kind: asset.kind, id: asset.id, title: asset.title, section: asset.section || null },
      ]);
    }
  };

  const pick = (item) => (trigger === '#' ? insertAsset(item) : insertMention(item));

  // The button route into the asset list, for people who would never
  // guess "#". Opens the popup anchored at the caret with an empty
  // query; the pick then inserts "#<title> " exactly as typing would.
  const openAssetPicker = () => {
    const ta = taRef.current;
    const caret = ta ? ta.selectionStart : value.length;
    setAnchor(caret);
    setTrigger('#');
    setQuery('');
    setActiveIdx(0);
    try { ta && ta.focus(); } catch (_) { /* noop */ }
  };

  const handleKeyDown = (e) => {
    if (trigger != null && matches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % matches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + matches.length) % matches.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pick(matches[activeIdx]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSuggestions();
        return;
      }
    }
    if (parentOnKeyDown) parentOnKeyDown(e);
  };

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        ref={taRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Let the click-on-row handler fire before closing —
          // setTimeout pushes this past the click bubbling.
          setTimeout(closeSuggestions, 150);
        }}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: '100%', padding: '10px 12px',
          border: '1px solid #cbd5e1', borderRadius: 8,
          fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
          boxSizing: 'border-box',
          ...style,
        }}
      />
      {assetsEnabled && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            // mousedown-preventDefault keeps focus (and the caret) in
            // the textarea so the pick lands where the user was typing.
            onMouseDown={(e) => e.preventDefault()}
            onClick={openAssetPicker}
            aria-label="Link a document or trailer from this room"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'none', border: 'none', padding: '2px 0',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              color: 'var(--room-accent, #7c3aed)', fontFamily: 'inherit',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            Link a document or trailer
          </button>
          <span style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)' }}>or type #</span>
        </div>
      )}
      {trigger != null && matches.length > 0 && (
        <div
          role="listbox"
          aria-label={trigger === '#' ? 'Documents and trailers in this room' : 'People to mention'}
          // Popup position: just above the textarea, anchored left.
          // Simpler than caret-aware floating, and visually clearer.
          style={{
            position: 'absolute', left: 0, bottom: '100%', marginBottom: 4,
            background: 'var(--room-card, #fff)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            minWidth: 240, maxWidth: 360, zIndex: 30,
            overflow: 'hidden',
          }}
        >
          {matches.map((item, i) => (
            <div
              key={trigger === '#' ? `${item.kind}:${item.id}` : item.id}
              role="option"
              aria-selected={i === activeIdx}
              onMouseDown={(e) => { e.preventDefault(); pick(item); }}
              onMouseEnter={() => setActiveIdx(i)}
              style={{
                padding: '8px 12px', cursor: 'pointer',
                background: i === activeIdx ? 'var(--room-border-soft, #f1f5f9)' : 'transparent',
                display: 'flex', flexDirection: 'column', gap: 2,
              }}
            >
              {trigger === '#' ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--room-text-strong, #0f172a)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: 'var(--room-text-muted, #64748b)', display: 'inline-flex' }}>
                      {item.kind === 'trailer' ? TRAILER_ICON : DOC_ICON}
                    </span>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>
                    {assetKindLabel(item.kind)}{item.subtitle ? ` · ${item.subtitle}` : ''}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--room-text-strong, #0f172a)' }}>
                    {item.fullName}
                    {!item.email && (
                      <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 500, marginLeft: 6 }}>
                        no email — won't get notified
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>
                    {item.side === 'buyer' ? 'Buyer side' : 'Your team'}
                    {item.role ? ` · ${item.role}` : ''}
                    {item.email ? ` · ${item.email}` : ''}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Strip mentions whose @<displayName> the user backspaced out of the
 * body before posting. Keeps the persisted mentions array honest so
 * notifications don't fire for people the sender removed from the
 * message text. Case-insensitive match.
 */
export function filterMentionsAgainstBody(body, mentions) {
  if (!Array.isArray(mentions) || mentions.length === 0) return [];
  const lower = String(body || '').toLowerCase();
  return mentions.filter((m) => {
    if (!m || !m.displayName) return false;
    return lower.includes(`@${m.displayName.toLowerCase()}`);
  });
}

/**
 * Same for asset references: keep only refs whose #<title> is still in
 * the body, so a pick the sender deleted never ships as a chip.
 */
export function filterAssetRefsAgainstBody(body, refs) {
  if (!Array.isArray(refs) || refs.length === 0) return [];
  const lower = String(body || '').toLowerCase();
  return refs.filter((r) => r && r.title && lower.includes(`#${String(r.title).toLowerCase()}`));
}

/**
 * Render a message body with @<displayName> swapped for mention chips
 * and #<title> swapped for asset chips. Returns an array of React nodes
 * (string segments + chip components). Used by seller-side, prospect-
 * side and inbox thread displays so the visual treatment is consistent.
 *
 * Options:
 *   highlightEmail   — the viewer's email; a mention of them is amber.
 *   assetRefs        — [{ kind, id, title, section }] on the message.
 *   onAssetClick     — (ref) => void; when given, an AVAILABLE asset chip
 *                      is a button that calls it (the prospect view
 *                      navigates to the asset). Without it chips are
 *                      inert labels (seller side, inbox).
 *   isAssetAvailable — (ref) => boolean; an unavailable asset (hidden
 *                      or removed since the message was written) renders
 *                      as a muted, non-clickable chip. Defaults to true.
 */
export function renderBodyWithMentions(body, mentions, {
  highlightEmail = null,
  assetRefs = null,
  onAssetClick = null,
  isAssetAvailable = null,
} = {}) {
  const mentionNeedles = (Array.isArray(mentions) ? mentions : [])
    .filter((m) => m && m.displayName)
    .map((m) => ({ needle: `@${m.displayName}`, type: 'mention', item: m }));
  const assetNeedles = (Array.isArray(assetRefs) ? assetRefs : [])
    .filter((r) => r && r.title)
    .map((r) => ({ needle: `#${r.title}`, type: 'asset', item: r }));
  if (mentionNeedles.length === 0 && assetNeedles.length === 0) {
    return body || '';
  }
  // Sort by needle length DESC so longer names match first ("Jane Doe
  // Smith" before "Jane"). Otherwise we'd chip the wrong substring on
  // overlapping names.
  const sorted = [...mentionNeedles, ...assetNeedles].sort((a, b) => b.needle.length - a.needle.length);

  const out = [];
  let remaining = String(body || '');
  let key = 0;
  while (remaining.length > 0) {
    let earliest = -1;
    let matched = null;
    const lowerRemaining = remaining.toLowerCase();
    for (const n of sorted) {
      const idx = lowerRemaining.indexOf(n.needle.toLowerCase());
      if (idx !== -1 && (earliest === -1 || idx < earliest)) {
        earliest = idx;
        matched = n;
      }
    }
    if (earliest === -1) {
      out.push(remaining);
      break;
    }
    if (earliest > 0) out.push(remaining.slice(0, earliest));
    if (matched.type === 'mention') {
      const m = matched.item;
      const isMe = highlightEmail
        && m.email
        && m.email.toLowerCase() === String(highlightEmail).toLowerCase();
      out.push(
        <span
          key={`mention-${key++}`}
          title={isMe ? 'You were mentioned' : `Mention: ${m.displayName}`}
          style={{
            display: 'inline-block',
            padding: '0 6px', borderRadius: 4,
            background: isMe ? 'rgba(245, 158, 11, 0.18)' : 'rgba(99, 102, 241, 0.10)',
            color: isMe ? '#92400e' : '#4f46e5',
            fontWeight: 600,
            border: `1px solid ${isMe ? 'rgba(245, 158, 11, 0.35)' : 'rgba(99, 102, 241, 0.22)'}`,
          }}
        >
          @{m.displayName}
        </span>
      );
    } else {
      out.push(<AssetChip key={`asset-${key++}`} refItem={matched.item} onAssetClick={onAssetClick} isAssetAvailable={isAssetAvailable} />);
    }
    remaining = remaining.slice(earliest + matched.needle.length);
  }
  return out;
}

function AssetChip({ refItem, onAssetClick, isAssetAvailable }) {
  const available = typeof isAssetAvailable === 'function' ? Boolean(isAssetAvailable(refItem)) : true;
  const clickable = available && typeof onAssetClick === 'function';
  const icon = refItem.kind === 'trailer' ? TRAILER_ICON : DOC_ICON;
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '0 7px', borderRadius: 4,
    fontWeight: 600, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit',
    verticalAlign: 'baseline',
  };
  if (!available) {
    return (
      <span
        title={`${assetKindLabel(refItem.kind)} no longer available in this room`}
        style={{
          ...base,
          background: 'rgba(148, 163, 184, 0.14)', color: '#64748b',
          border: '1px solid rgba(148, 163, 184, 0.35)', textDecoration: 'line-through',
        }}
      >
        {icon}{refItem.title}
      </span>
    );
  }
  if (clickable) {
    return (
      <button
        type="button"
        onClick={() => onAssetClick(refItem)}
        title={`Open ${assetKindLabel(refItem.kind).toLowerCase()}: ${refItem.title}`}
        style={{
          ...base, cursor: 'pointer',
          background: 'rgba(16, 185, 129, 0.10)', color: '#047857',
          border: '1px solid rgba(16, 185, 129, 0.30)',
        }}
      >
        {icon}{refItem.title}
      </button>
    );
  }
  return (
    <span
      title={`${assetKindLabel(refItem.kind)} in this room: ${refItem.title}`}
      style={{
        ...base,
        background: 'rgba(16, 185, 129, 0.10)', color: '#047857',
        border: '1px solid rgba(16, 185, 129, 0.30)',
      }}
    >
      {icon}{refItem.title}
    </span>
  );
}

/**
 * Build the picker list for a room from the seller-side detail payload:
 * documents the prospect can see + completed trailers. Same title rule
 * as the server's resolver (utils/roomAssetRefs.js in the backend), so
 * the "#<title>" typed here matches the chip the reader sees.
 */
export function roomAssetsFromData(data) {
  if (!data) return [];
  const docs = (data.docs || [])
    .filter((d) => !d.prospectHidden)
    .map((d) => ({
      kind: 'doc',
      id: d.id,
      title: d.displayTitle || d.filename,
      section: d.category === 'customer_story' ? 'customer_stories' : 'documents',
      subtitle: d.pageCount ? `${d.pageCount} page${d.pageCount === 1 ? '' : 's'}` : null,
    }));
  const trailers = (data.trailers || [])
    .filter((t) => t.status === 'completed')
    .map((t) => ({
      kind: 'trailer',
      id: t.id,
      title: t.prospectName || t.filename,
      section: 'demos',
      subtitle: null,
    }));
  return [...docs, ...trailers];
}
